import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { getPaginasMaisAcessadas } from '../../../../api/reportApi';
import TableContainer from '../../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';
import AppMetaBadge from './AppMetaBadge';

interface PageAccessCardProps {
  campaignId: string;
}

export interface PageAccessCardRef {
  refresh: () => void;
}

const PageAccessCard = forwardRef<PageAccessCardRef, PageAccessCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const pageData = await getPaginasMaisAcessadas(parseInt(campaignId));
        setData(pageData);
      } catch (err) {
        console.error('Error fetching page access data:', err);
        setError('Erro ao carregar dados de páginas acessadas');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId, fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={`${styles.reportCard} h-full w-full`}>
          <CardHeader className={styles.cardHeader}>
            <div className="flex items-center">
              <div className="bg-info bg-opacity-10 rounded-full p-2 mr-3">
                <i className="ri-file-list-line text-info text-2xl"></i>
              </div>
              <div>
                <h4 className={`${styles.cardTitle} mb-0`}>Páginas mais acessadas</h4>
                <small className="text-muted">Path names com maior tráfego</small>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center text-destructive">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={`${styles.reportCard} h-full w-full`}>
          <CardHeader className={styles.cardHeader}>
            <div className="flex items-center">
              <div className="bg-info bg-opacity-10 rounded-full p-2 mr-3">
                <i className="ri-file-list-line text-info text-2xl"></i>
              </div>
              <div>
                <h4 className={`${styles.cardTitle} mb-0`}>Páginas mais acessadas</h4>
                <small className="text-muted">Path names com maior tráfego</small>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted">
              <i className="ri-information-line mr-2"></i>
              Nenhuma página acessada encontrada
            </div>
          </CardContent>
        </Card>
      );
    }

    // Calculate total impressions for percentage calculation
    const totalImpressions = data.reduce((sum, item) => sum + ((item.impression || 0) + (item.start || 0)), 0);

    // Column definitions for the table
    const columns = [
      {
        header: 'Página',
        accessorKey: 'originPath',
        cell: (row: any) => {
          const original = row.row.original;

          if (original.isApp) {
            const label = original.appName || original.originHostname || 'App';
            return <AppMetaBadge label={label} iconUrl={original.iconUrl} />;
          }

          const path = row.getValue() || 'Unknown';
          return (
            <div className="fw-medium text-truncate" style={{ maxWidth: '120px' }} title={path}>
              {path}
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: 'impression',
        header: 'Impressões',
        accessorFn: (row: any) => (row.impression || 0) + (row.start || 0),
        cell: (row: any) => {
          const value = row.getValue() || 0;
          const percentage = totalImpressions > 0 ? ((value / totalImpressions) * 100).toFixed(1) : '0.0';

          return (
            <div className="text-end">
              <span className="font-semibold">{numeral(value).format(numeralInteger)}</span>
              <Badge variant="secondary" className="bg-primary bg-opacity-10 text-primary px-1 py-1 ml-1">
                {percentage}%
              </Badge>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Visíveis',
        accessorKey: 'viewable',
        cell: (row: any) => (
          <div className="text-end">
            <span className="font-semibold">{numeral(row.getValue() || 0).format(numeralInteger)}</span>
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Cliques',
        accessorKey: 'click',
        cell: (row: any) => {
          const click = row.getValue() || 0;
          const impression = row.original?.impression || 0;
          const start = row.original?.start || 0;
          const totalImpression = impression + start;
          const ctr = totalImpression > 0 ? ((click / totalImpression) * 100).toFixed(2) : '0.00';
          
          return (
            <div className="text-end">
              <span className="font-semibold">{numeral(click).format(numeralInteger)}</span>
              <Badge variant="secondary" className="bg-info bg-opacity-10 text-info px-1 py-1 ml-1">
                {ctr}%
              </Badge>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    return (
      <Card className={`${styles.reportCard} h-full w-full`}>
        <CardHeader className={styles.cardHeader}>
          <div className="flex items-center">
            <div className="bg-info bg-opacity-10 rounded-full p-2 mr-3">
              <i className="ri-file-list-line text-info text-2xl"></i>
            </div>
            <div>
              <h4 className={`${styles.cardTitle} mb-0`}>Páginas mais acessadas</h4>
              <small className="text-muted">Path names com maior tráfego</small>
            </div>
            <div className="ml-auto">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <i
                      className="ri-question-line text-muted text-lg cursor-help"
                    ></i>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Páginas onde os anúncios foram mais exibidos, ordenadas por impressões totais.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-2">
          <TableContainer
            divClass="table-responsive"
            columns={columns}
            data={data}
            isGlobalFilter={false}
            customPageSize={5}
            initialSorting={[{ id: 'impression', desc: true }]}
          />
        </CardContent>
      </Card>
    );
  }
);

PageAccessCard.displayName = 'PageAccessCard';

export default PageAccessCard;
